import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next'
import {PrismicRichText, SliceZone} from '@prismicio/react'
import dayjs from 'dayjs'
import {ParentUid} from 'pages/[uid]/[subpageUid]'
import {
  ArticleDocument,
  AuthorDocumentData,
  ButtonDocument,
  PageDocumentData,
  ArticleDocumentData,
  CustomerStoryDocumentData,
  HomePageDocumentData,
  NavigationDocumentData,
} from 'prismicio-types'
import {Fragment, memo} from 'react'
import {components} from 'slices'
import {Layout} from 'src/common/Layout'
import {Meta} from 'src/common/Meta'

export const Article = memo(function Article(props: {
  navigationData: NavigationDocumentData
  page: ArticleDocument
  parentUids?: Array<ParentUid>
  breadcrumbs?:
    | PageDocumentData['breadcrumbs']
    | ArticleDocumentData['breadcrumbs']
    | CustomerStoryDocumentData['breadcrumbs']
    | HomePageDocumentData['breadcrumbs']
  buttonsData?: Array<ButtonDocument>
}) {
  return (
    <Layout
      alternateLanguages={
        props.parentUids
          ? props.page.alternate_languages.map((altLang) => ({
              ...altLang,
              parentUid: props.parentUids?.find((parentUid) => parentUid.lang === altLang.lang)?.uid,
            }))
          : props.page.alternate_languages
      }
      navigationData={props.navigationData}
      className="!bg-ffffff"
      mainClassName="pb-40"
      pageUid={props.page.uid}
      locale={props.page.lang}
      breadcrumbs={props.breadcrumbs}
      parentUids={props.parentUids}
    >
      <Meta
        {...(isFilled.keyText(props.page.data.meta_description) && {description: props.page.data.meta_description})}
        {...(isFilled.keyText(props.page.data.meta_title) && {title: props.page.data.meta_title})}
        {...(isFilled.image(props.page.data.meta_image) && {imageUrl: props.page.data.meta_image.url})}
      />

      <section className="mb-16 grid grid-cols-main border-b border-b-dcdbda pb-24 pt-16 sm:pb-20 md:pb-16 md:pt-20 lg:pb-12 lg:pt-24">
        <div className="col-[inner] mb-6 flex h-[22px] items-center justify-self-center rounded-4 bg-5a7269 px-2 text-12 font-medium leading-4 tracking-common text-ffffff">
          {props.page.data.tag}
        </div>

        <PrismicRichText
          components={{
            heading1: ({children}) => (
              <h1 className="col-[inner] mb-8 max-w-[46.25rem] justify-self-center text-center text-28 leading-11 -tracking-common text-2a2928 sm:mb-10 sm:text-36 md:text-40 md:leading-12 lg:text-48 lg:leading-14">
                {children}
              </h1>
            ),
          }}
          field={props.page.data.title}
        />

        <div className="col-[inner] mb-8 flex flex-wrap items-center justify-self-center text-666563 sm:mb-10 md:mb-14">
          {isFilled.contentRelationship(props.page.data.author) && (
            <Fragment>
              <PrismicNextImage
                className="rounded-full"
                field={(props.page.data.author.data as AuthorDocumentData)?.photo}
                height={24}
                width={24}
                quality={90}
              />

              <span className="ml-2 text-14 leading-5">
                {(props.page.data.author.data as AuthorDocumentData)?.name}
              </span>
            </Fragment>
          )}

          <span className="ml-3 border-l-b8b6b4 pl-3 text-15 leading-5 sm:border-l">
            {dayjs(
              isFilled.date(props.page.data.date)
                ? props.page.data.date
                : props.page.last_publication_date || props.page.first_publication_date,
            ).format('MMMM DD, YYYY')}
          </span>
        </div>

        <PrismicNextImage
          className="col-[inner] w-full max-w-[58.125rem] justify-self-center rounded-[32px]"
          field={props.page.data.poster}
          quality={90}
          priority
          sizes="(max-width: 640px) calc(100vw - 48px), (max-width: 1024px), 930px"
        />
      </section>

      <SliceZone
        components={components}
        slices={props.page.data.slices}
        context={{
          pageUid: props.page.uid,
          buttonsData: props.buttonsData,
        }}
      />
    </Layout>
  )
})
